import { IColumn } from '@fluentui/react';
import AppContext from 'App/AppContext';
import {
  IPPersona,
  IPPersonaSize,
  IPPrimaryButton,
  IPSelectionMode,
  IPSeparator,
  IPSpinner,
  IPSpinnerSize,
  IPText,
  IPToggle,
} from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { LatestApproval } from 'components/Approval/LatestApproval';
import GenericModal from 'components/Dialogs/GenericModal';
import BaseTableList from 'components/TableList/BaseTableList';
import ScrollableStackItem from 'components/Utils/ScrollableStackItem';
import { Approval } from 'models/approval';
import ResourceLink, { PublicationResult } from 'models/resourceLink';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiGetLinkPublicationHistory } from 'services/Api/linkService';
import { apiRequest } from 'services/Auth/authConfig';
import { toLocaleDateTimeNoSeconds } from 'utils/datetime';
import { sortOnVersion } from 'utils/sorting';
import { LinkPublicationModal } from './LinkPublicationModal';

interface ILinkPublicationHistoryModalProps {
  isOpen: boolean;
  link: ResourceLink;
  onClose: () => void;
  onUpdate?: (links: ResourceLink[]) => void;
  allowPublish?: boolean;
}

export const LinkPublicationHistoryModal = (props: ILinkPublicationHistoryModalProps) => {
  const { t } = useTranslation(['translation', 'publication', 'sharepoint']);
  const appContext = useContext(AppContext);
  const [results, setResults] = useState<PublicationResult[]>([]);
  const [resultsDisplay, setResultsDisplay] = useState<PublicationResult[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showMinorVersions, setShowMinorVersions] = useState<boolean>(false);
  const [showPublicationModal, setShowPublicationModal] = useState<boolean>(false);
  const [selectedLink, setSelectedLink] = useState<ResourceLink>(props.link);

  //
  // Effects
  //
  useEffect(() => {
    if (props.isOpen) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isOpen]);

  const loadData = async () => {
    if (!selectedLink) return;

    try {
      setIsLoading(true);
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const publicationResults = await apiGetLinkPublicationHistory(
        selectedLink,
        accessToken,
        appContext.globalDataCache,
      );
      publicationResults.sort((a, b) => sortOnVersion(b.newVersion, a.newVersion));
      setResults(publicationResults);
      const publicationResultsDisplay = filterMinorVersions(publicationResults, showMinorVersions);
      setResultsDisplay(publicationResultsDisplay);
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const filterMinorVersions = (publicationResults: PublicationResult[], show: boolean): PublicationResult[] => {
    if (!show) {
      //filter out minor versions, except the first one
      return publicationResults.filter((pr, idx) => idx === 0 || ResourceLink.isMajorVersion(pr.newVersion));
    } else {
      return publicationResults;
    }
  };

  //
  // Columns list view
  //
  const getColumns = (): IColumn[] => {
    const allColumns: IColumn[] = [
      {
        key: 'version',
        name: t('publication:Publish.PublicationHistoryModal.Columns.Version'),
        minWidth: 70,
        maxWidth: 70,
      },
      {
        key: 'created',
        name: t('publication:Publish.PublicationHistoryModal.Columns.Created'),
        minWidth: 150,
        maxWidth: 150,
      },
      {
        key: 'createdBy',
        name: t('publication:Publish.PublicationHistoryModal.Columns.CreatedBy'),
        minWidth: 150,
        maxWidth: 250,
        isResizable: true,
      },
      {
        key: 'lastModified',
        name: t('publication:Publish.PublicationHistoryModal.Columns.LastModified'),
        minWidth: 150,
        maxWidth: 150,
      },
      {
        key: 'smallChange',
        name: t('publication:Publish.PublicationHistoryModal.Columns.SmallChange'),
        minWidth: 100,
        maxWidth: 100,
      },
      {
        key: 'approval',
        name: t('publication:Publish.PublicationHistoryModal.Columns.Approval'),
        minWidth: 200,
        maxWidth: 400,
        isResizable: true,
        isMultiline: true,
      },
    ];

    return allColumns;
  };

  const [columnsList, setColumnsList] = useState<IColumn[]>(getColumns());

  const onRenderCol = (item: PublicationResult, column: IColumn, index?: number): JSX.Element | null => {
    if (!item) return null;

    switch (column.key) {
      case 'version':
        return <IPText>{item.newVersion}</IPText>;
      case 'created':
        if (!item.publicationId) {
          if (index === 0 && props.allowPublish) {
            return (
              <IPPrimaryButton
                text={t('publication:Publish.PublicationHistoryModal.Publish')}
                onClick={() => setShowPublicationModal(true)}
              />
            );
          } else {
            return <IPText>{'-'}</IPText>;
          }
        }

        return <IPText>{toLocaleDateTimeNoSeconds(item.created)}</IPText>;
      case 'createdBy':
        if (!item.publicationId || !item.createdById) return <IPText>{'-'}</IPText>;
        const user = appContext.globalDataCache.users.get(item.createdById);

        return <IPPersona imageUrl={user?.getPhotoURLObj()} size={IPPersonaSize.size24} text={user?.name || ''} />;
      case 'lastModified':
        return <IPText>{toLocaleDateTimeNoSeconds(item.lastModified)}</IPText>;
      case 'smallChange':
        if (!item.publicationId) return <IPText>{'-'}</IPText>;

        return (
          <IPText>{item.smallChange ? t('translation:General.Words.Yes') : t('translation:General.Words.No')}</IPText>
        );
      case 'approval':
        if (!item.approvalTask) return <IPText>{'-'}</IPText>;

        const approval = new Approval();
        approval.mainTask = item.approvalTask;
        approval.approvalTasks = item.approvalTask.instances ?? [];
        approval.link = props.link;

        return <LatestApproval link={props.link} approvals={[approval]} showHistoryLink={false} />;

      default:
        return null;
    }
  };

  //
  // Main render
  //
  return (
    <GenericModal
      isOpen={props.isOpen}
      onClose={props.onClose}
      title={t('publication:Publish.PublicationHistoryModal.Title')}
      subTitle={t('publication:Publish.PublicationHistoryModal.SubText', { name: selectedLink?.linkName })}
      height={700}
      width={1200}
      maxWidth={'min(90vw, 1200px)'}
      maxHeight={'90vh'}
      minHeight={300}
      minWidth={300}
    >
      <IPStack verticalFill>
        <IPStack.Item>
          <IPSeparator />
        </IPStack.Item>
        <IPStack.Item>
          <IPToggle
            label={t('publication:Publish.PublicationHistoryModal.ShowMinorVersions')}
            checked={showMinorVersions}
            onChange={(ev, checked) => {
              setShowMinorVersions(checked ?? false);
              const filteredResults = filterMinorVersions(results, checked ?? false);
              setResultsDisplay(filteredResults);
            }}
            onText={t('translation:General.Toggle.On')}
            offText={t('translation:General.Toggle.Off')}
          />
        </IPStack.Item>
        {isLoading && (
          <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
            <IPSpinner size={IPSpinnerSize.large} />
          </IPStack>
        )}
        {!isLoading && (
          <ScrollableStackItem>
            <BaseTableList
              compact
              items={resultsDisplay}
              columns={columnsList}
              selectionMode={IPSelectionMode.none}
              onRenderCol={onRenderCol}
              setColumns={setColumnsList}
              disableSort={true}
            />
          </ScrollableStackItem>
        )}
      </IPStack>
      {showPublicationModal && (
        <LinkPublicationModal
          isOpen={true}
          links={[selectedLink]}
          onUpdate={(links) => {
            setSelectedLink(links[0]);
            if (props.onUpdate) props.onUpdate(links);
          }}
          onClose={() => {
            setShowPublicationModal(false);
            loadData();
          }}
        />
      )}
    </GenericModal>
  );
};
